import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, AlertTitle, Box, Button, CircularProgress, Grid, TextField, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { ImageAnalysisSlideJobStatus } from 'api/jobs';
import { runManualPostIngestPipeline } from 'api/platform';
import { useTrackActionFormDirty } from 'components/ActionDialog/actionFormDirtyState';
import { useImageAnalysisStatus } from 'components/StudyDashboard/Workflow/useImageAnalysisStatus';
import { MARK_DONE_FAILURE_MESSAGE } from 'components/StudyDashboard/Workflow/useWorkflowStepDoneState';
import { JobStatus, JobType } from 'interfaces/job';
import { Permission } from 'interfaces/permissionOption';
import { BrightfieldPostIngestStep, MifPostIngestStep, Study } from 'interfaces/study';
import { every, filter, find, flatMap, includes, keyBy, last, map, size, sumBy } from 'lodash';
import { useSnackbar } from 'notistack';
import React, { ReactElement, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { CasesParams } from 'utils/useCasesParams/types';
import { usePermissions } from 'utils/usePermissions';
import { useStainTypeIdToDisplayName } from 'utils/useStainTypeIdToDisplayName';
import * as yup from 'yup';

import { ActionStepper } from '../ActionStepper';
import { ChannelsController } from '../ChannelsController';
import { JobNameAndDescriptionStep } from '../utils';
import {
  PostIngestFamilySelection,
  PostIngestStainFamily,
  usePostIngestPipelineSelection,
} from './usePostIngestPipelineSelection';

const SNACK_BAR_KEY_RUN_MANUAL_POST_INGEST_PIPELINE = 'RUN_MANUAL_POST_INGEST_PIPELINE';
const REQUIRED_BRIGHTFIELD_PIPELINE_ACTIONS = ['Cloud optimization', 'Tissue segmentation'];
const REQUIRED_MIF_PIPELINE_ACTIONS = ['Cloud optimization', 'Tissue segmentation', 'Histogram creation'];
const DNA_TILE_PERCENTILE_CAPS_ACTION = 'DNA tile percentile caps';

const brightfieldStepLabels: Record<BrightfieldPostIngestStep, string> = {
  cellSegmentation: 'Cell segmentation',
};

const mifStepLabels: Record<MifPostIngestStep, string> = {
  normalization: 'Normalization',
  cellSegmentation: 'Cell segmentation',
};

const stainFamilyLabels: Record<PostIngestStainFamily, string> = {
  brightfield: 'Brightfield',
  mIF: 'mIF',
};

export interface ManualPostIngestFormValues {
  dnaChannels?: string[];
  rerunReason?: string;
  jobName?: string;
  jobDescription?: string;
}

const getValidationSchema = (requiresRerunReason: boolean) => [
  yup.object({
    rerunReason: requiresRerunReason
      ? yup.string().trim().required('Enter a rerun reason.')
      : yup.string().optional().nullable(),
  }),
  yup.object({
    jobName: yup.string().optional().nullable(),
    jobDescription: yup.string().optional().nullable(),
  }),
];

const defaultValues: ManualPostIngestFormValues = {
  dnaChannels: [],
  rerunReason: '',
  jobName: '',
  jobDescription: '',
};

// Statuses that mean a slide already has, or will soon have, this analysis.
const PREVIOUS_RUN_STATUSES = [JobStatus.Pending, JobStatus.Running, JobStatus.Completed];

export interface CaseImageAnalysisStepControl {
  stepLabel: string;
  isDone: boolean;
  canMarkDone: boolean;
  onMarkDone: () => Promise<'approved' | 'cancelled' | 'failed'>;
}

interface Props {
  onClose: (event?: any, force?: boolean) => void;
  casesParams: CasesParams;
  study?: Study;
  caseImageAnalysisStep?: CaseImageAnalysisStepControl;
}

// Job types the backend pipeline creates per stain family; histogram and cloud optimization are not tracked.
const getPipelineStatusJobTypes = (stainFamily: PostIngestStainFamily, study?: Study): JobType[] => {
  if (stainFamily === 'brightfield') {
    return [JobType.BrightfieldTissueSegmentationMask];
  }
  const additionalSteps = study?.settings?.postIngestPipeline?.mIF?.additionalSteps ?? [];
  return [
    JobType.MultiplexTissueSegmentationMask,
    ...(includes(additionalSteps, 'normalization') ? [JobType.MultiplexNormalization] : []),
    ...(includes(additionalSteps, 'cellSegmentation') ? [JobType.MultiplexCellSegmentation] : []),
  ];
};

export const getPreviousRunState = ({
  familySelections,
  statuses,
  study,
}: {
  familySelections: PostIngestFamilySelection[];
  statuses: ImageAnalysisSlideJobStatus[];
  study?: Study;
}) => {
  const statusBySlideAndJobType = keyBy(statuses, ({ slideId, jobType }) => `${slideId}:${jobType}`);
  const latestStatuses = flatMap(familySelections, ({ stainFamily, slides }) => {
    const jobTypes = getPipelineStatusJobTypes(stainFamily, study);
    return flatMap(slides, ({ slideId }) =>
      map(jobTypes, (jobType) => statusBySlideAndJobType[`${slideId}:${jobType}`]?.status)
    );
  });
  const hasRunForAllSlides =
    size(latestStatuses) > 0 && every(latestStatuses, (status) => includes(PREVIOUS_RUN_STATUSES, status));
  return {
    hasRunForAllSlides,
    hasCompletedForAllSlides: hasRunForAllSlides && every(latestStatuses, (status) => status === JobStatus.Completed),
  };
};

const buildJobDescription = (rerunReason?: string, jobDescription?: string) => {
  const trimmedReason = rerunReason?.trim();
  if (!trimmedReason) {
    return jobDescription || undefined;
  }
  const rerunLine = `Rerun reason: ${trimmedReason}`;
  return jobDescription ? `${rerunLine}\n\n${jobDescription}` : rerunLine;
};

const getConfiguredPipelineActions = (stainFamily: PostIngestStainFamily, study?: Study): string[] => {
  if (stainFamily === 'brightfield') {
    const additionalSteps = study?.settings?.postIngestPipeline?.brightfield?.additionalSteps ?? [];
    return [...REQUIRED_BRIGHTFIELD_PIPELINE_ACTIONS, ...map(additionalSteps, (step) => brightfieldStepLabels[step])];
  }

  const mifSettings = study?.settings?.postIngestPipeline?.mIF;
  const additionalSteps = mifSettings?.additionalSteps ?? [];
  return [
    ...REQUIRED_MIF_PIPELINE_ACTIONS,
    ...map(additionalSteps, (step) => mifStepLabels[step]),
    ...(mifSettings?.dnaTilePercentileCapsEnabled ? [DNA_TILE_PERCENTILE_CAPS_ACTION] : []),
  ];
};

const buildFamilyRequests = ({
  familySelections,
  studyId,
  values,
  dnaChannel,
}: {
  familySelections: PostIngestFamilySelection[];
  studyId: string;
  values: ManualPostIngestFormValues;
  dnaChannel?: string;
}) => {
  const jobDescription = buildJobDescription(values.rerunReason, values.jobDescription);
  return map(familySelections, ({ stainFamily, casesParams }) => {
    const enteredJobName = values.jobName?.trim();
    const familyJobName =
      enteredJobName && size(familySelections) > 1
        ? `${enteredJobName} - ${stainFamilyLabels[stainFamily]}`
        : enteredJobName;

    return {
      studyId,
      stainFamily,
      slideIds: casesParams.slideIdsToInclude ?? [],
      ...(stainFamily === 'mIF' ? { dnaChannel } : {}),
      jobName: familyJobName || undefined,
      jobDescription,
    };
  });
};

export const RunManualPostIngestPipeline = ({
  onClose,
  casesParams,
  study,
  caseImageAnalysisStep,
}: Props): ReactElement => {
  const studyId = casesParams.studyId ?? casesParams.filters?.studyId;
  const labId = casesParams.labId;
  const { hasPermission } = usePermissions();
  const { familySelections, isLoadingSelection, selectionError, hasSelectedSlides } =
    usePostIngestPipelineSelection(casesParams);
  const selectedSlideIds = useMemo(
    () => flatMap(familySelections, ({ slides }) => map(slides, 'slideId')),
    [familySelections]
  );
  const canCheckPreviousRuns = Boolean(
    studyId && labId && hasPermission(Permission.ViewJobs) && size(selectedSlideIds)
  );
  const previousRunsQuery = useImageAnalysisStatus({
    studyId: studyId ?? '',
    labId: labId ?? '',
    slideIds: selectedSlideIds,
    enabled: canCheckPreviousRuns,
  });
  const isLoadingPreviousRuns = canCheckPreviousRuns && previousRunsQuery.isLoading;
  const { hasRunForAllSlides, hasCompletedForAllSlides } = useMemo(
    () => getPreviousRunState({ familySelections, statuses: previousRunsQuery.data?.statuses ?? [], study }),
    [familySelections, previousRunsQuery.data?.statuses, study]
  );
  const requiresRerunReason = canCheckPreviousRuns && hasRunForAllSlides;
  const [markDoneError, setMarkDoneError] = useState<string>();
  const [isMarkingDone, setIsMarkingDone] = useState(false);
  const pipelineReviews = useMemo(
    () =>
      map(familySelections, (selection) => ({
        ...selection,
        actions: getConfiguredPipelineActions(selection.stainFamily, study),
      })),
    [familySelections, study]
  );
  const invalidPipelineReviews = useMemo(
    () => filter(pipelineReviews, ({ canRunPostIngestPipeline }) => !canRunPostIngestPipeline),
    [pipelineReviews]
  );
  const finishDisabledMessage =
    size(invalidPipelineReviews) >= 1
      ? size(invalidPipelineReviews) === 1
        ? 'Resolve the blocked initial image analysis prerequisite to continue.'
        : `${size(
            invalidPipelineReviews
          )} selected stain-family pipelines are blocked. Adjust the slide selection to continue.`
      : undefined;
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const activeStateHandlers = useState(0);
  const [activeStep, setActiveStep] = activeStateHandlers;
  const { stainTypeIdToDisplayName } = useStainTypeIdToDisplayName();
  const mifSelection = find(pipelineReviews, ({ stainFamily }) => stainFamily === 'mIF');
  const availableDnaChannels = mifSelection?.availableDnaChannels ?? [];
  const defaultDnaChannel = last(availableDnaChannels);
  const hasMultipleDnaChannels = size(availableDnaChannels) > 1;
  const dnaChannelOptions = useMemo(
    () =>
      map(availableDnaChannels, (dnaChannel) => ({
        value: dnaChannel,
        text: stainTypeIdToDisplayName(dnaChannel),
      })),
    [availableDnaChannels, stainTypeIdToDisplayName]
  );
  const formMethods = useForm<ManualPostIngestFormValues>({
    mode: 'onChange',
    resolver: yupResolver(getValidationSchema(requiresRerunReason)[activeStep]),
    defaultValues,
  });
  const {
    register,
    handleSubmit,
    control,
    watch,
    trigger,
    formState: { errors },
  } = formMethods;
  const selectedDnaChannel = last(watch('dnaChannels') || []);
  const effectiveDnaChannel = hasMultipleDnaChannels ? selectedDnaChannel : defaultDnaChannel;
  const isRerunReasonMissing = requiresRerunReason && !watch('rerunReason')?.trim();
  useTrackActionFormDirty(formMethods.formState.isDirty);
  const [isStepFailed, setIsStepFailed] = useState<Record<number, boolean>>({ 0: false, 1: false });
  const showMarkDoneAction = Boolean(
    caseImageAnalysisStep &&
      hasCompletedForAllSlides &&
      !caseImageAnalysisStep.isDone &&
      caseImageAnalysisStep.canMarkDone
  );
  const previousRunsTitle = hasCompletedForAllSlides
    ? 'All selected slides already completed initial image analysis.'
    : 'All selected slides already have initial image analysis pending, running, or completed.';
  const previousRunsGuidance = !hasCompletedForAllSlides
    ? 'Wait for the current jobs, or enter a reason to run it again.'
    : caseImageAnalysisStep?.isDone
    ? `${caseImageAnalysisStep.stepLabel} is already done. Enter a reason to run it again.`
    : showMarkDoneAction
    ? 'Mark this step as done instead, or enter a reason to run it again.'
    : 'Enter a reason to run it again.';

  const markStepDone = async () => {
    if (!caseImageAnalysisStep) {
      return;
    }
    setMarkDoneError(undefined);
    setIsMarkingDone(true);
    const result = await caseImageAnalysisStep.onMarkDone();
    setIsMarkingDone(false);
    if (result === 'approved') {
      onClose(undefined, true);
    } else if (result === 'failed') {
      setMarkDoneError(MARK_DONE_FAILURE_MESSAGE);
    }
  };

  const checkValidationAndSetIsStepFailed = async (stepIndex: number) => {
    const isValid = await trigger();
    setIsStepFailed((previousState) => ({ ...previousState, [stepIndex]: !isValid }));
    return isValid;
  };

  const runManualPostIngestPipelineMutation = useMutation(
    (requests: ReturnType<typeof buildFamilyRequests>) =>
      Promise.all(map(requests, (request) => runManualPostIngestPipeline(request))),
    {
      onError: () => {
        enqueueSnackbar('Could not start initial image analysis', { variant: 'error' });
      },
      onSuccess: () => {
        enqueueSnackbar('Initial image analysis started', { variant: 'success' });
      },
      onSettled() {
        closeSnackbar(SNACK_BAR_KEY_RUN_MANUAL_POST_INGEST_PIPELINE);
      },
    }
  );

  const onSubmit = async (values: ManualPostIngestFormValues) => {
    if (!studyId || isRerunReasonMissing) {
      return;
    }

    enqueueSnackbar({
      variant: 'success',
      message: (
        <Grid container>
          <Grid item>
            <Typography>Waiting for initial image analysis to start</Typography>
          </Grid>
          <Grid item>
            <CircularProgress sx={{ marginLeft: 10 }} color="inherit" size={20} />
          </Grid>
        </Grid>
      ),
      key: SNACK_BAR_KEY_RUN_MANUAL_POST_INGEST_PIPELINE,
      autoHideDuration: null,
    });

    await runManualPostIngestPipelineMutation.mutateAsync(
      buildFamilyRequests({
        familySelections,
        studyId,
        values: { ...values, rerunReason: requiresRerunReason ? values.rerunReason : undefined },
        dnaChannel: effectiveDnaChannel,
      })
    );
    onClose(undefined, true);
  };

  const totalSlideCount = sumBy(familySelections, ({ slides }) => size(slides));
  const steps = [
    {
      label: 'Review pipeline actions',
      subLabel:
        size(familySelections) > 1
          ? `${size(familySelections)} pipelines for ${totalSlideCount} slides`
          : `${totalSlideCount} slides`,
      content: (
        <Grid container direction="column" spacing={2} mt={1} maxHeight="60vh" sx={{ overflowY: 'auto' }}>
          <Grid item>
            <Typography variant="body1">
              {size(familySelections) > 1
                ? 'The selection contains brightfield and mIF slides. Each stain family runs as a separate pipeline.'
                : `Initial image analysis will run for the selected ${
                    stainFamilyLabels[familySelections[0]?.stainFamily]
                  } slides.`}
            </Typography>
          </Grid>
          {requiresRerunReason ? (
            <Grid item>
              <Alert
                severity="warning"
                action={
                  showMarkDoneAction ? (
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      disabled={isMarkingDone}
                      onClick={() => void markStepDone()}
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      Mark as done
                    </Button>
                  ) : undefined
                }
              >
                <AlertTitle>{previousRunsTitle}</AlertTitle>
                {previousRunsGuidance}
              </Alert>
              {markDoneError ? (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {markDoneError}
                </Alert>
              ) : null}
              <TextField
                {...register('rerunReason')}
                label="Rerun reason"
                required
                fullWidth
                multiline
                minRows={2}
                error={Boolean(errors.rerunReason)}
                helperText={errors.rerunReason?.message || 'Saved in the job description.'}
                sx={{ mt: 2 }}
              />
            </Grid>
          ) : null}
          {previousRunsQuery.isError ? (
            <Grid item>
              <Alert severity="warning">Previous initial image analysis jobs could not be checked.</Alert>
            </Grid>
          ) : null}
          {map(
            pipelineReviews,
            ({
              stainFamily,
              slides,
              actions,
              canRunPostIngestPipeline,
              postIngestPipelineReason,
              availableDnaChannels: familyDnaChannels,
            }) => (
              <Grid item key={stainFamily}>
                <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
                  <Typography variant="subtitle2">
                    {stainFamilyLabels[stainFamily]} pipeline · {size(slides)} {size(slides) === 1 ? 'slide' : 'slides'}
                  </Typography>
                  {!canRunPostIngestPipeline && (
                    <Alert severity="error" sx={{ mt: 1.5 }}>
                      {postIngestPipelineReason}
                    </Alert>
                  )}
                  {stainFamily === 'mIF' && canRunPostIngestPipeline && size(familyDnaChannels) > 1 ? (
                    <Box sx={{ mt: 2 }}>
                      <ChannelsController
                        name="dnaChannels"
                        error={false}
                        methods={formMethods}
                        casesParams={casesParams}
                        channelOptions={dnaChannelOptions}
                        allowedChannelNames={familyDnaChannels}
                        defaultSelectedChannelsOnLoad={defaultDnaChannel ? [defaultDnaChannel] : undefined}
                        label="DNA channel"
                        placeholder="Select a DNA channel"
                        helperText="The last shared DNA channel is selected by default."
                        singleSelection
                      />
                    </Box>
                  ) : stainFamily === 'mIF' && canRunPostIngestPipeline && effectiveDnaChannel ? (
                    <Typography variant="body2" sx={{ mt: 2 }}>
                      DNA channel: {stainTypeIdToDisplayName(effectiveDnaChannel)}
                    </Typography>
                  ) : null}
                  <Box component="ol" sx={{ mb: 0, mt: 1, pl: 3 }}>
                    {map(actions, (action) => (
                      <Box component="li" key={action} sx={{ mb: 0.5 }}>
                        <Typography variant="body2">{action}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Grid>
            )
          )}
          <Grid item>
            <Typography variant="body2" color="text.secondary">
              Models and additional actions are resolved from the study&apos;s initial image analysis settings.
            </Typography>
          </Grid>
        </Grid>
      ),
      onNextOrBackClick: () => checkValidationAndSetIsStepFailed(0),
      continueDisabled: isRerunReasonMissing,
    },
    {
      label: 'Job name and description',
      subLabel: activeStep !== 1 && watch('jobName'),
      content: (
        <JobNameAndDescriptionStep
          control={control}
          errors={errors}
          register={register}
          onChangeName={() => checkValidationAndSetIsStepFailed(1)}
        />
      ),
      onNextOrBackClick: () => checkValidationAndSetIsStepFailed(1),
    },
  ];

  if (isLoadingSelection || isLoadingPreviousRuns) {
    return (
      <Alert severity="info">
        <AlertTitle>Checking slide selection</AlertTitle>
        {isLoadingSelection
          ? 'Resolving the selected slides and their stain families.'
          : 'Checking previous initial image analysis jobs for the selected slides.'}
      </Alert>
    );
  }

  if (selectionError || !studyId || !hasSelectedSlides || !size(familySelections)) {
    return (
      <Alert severity="error">
        <AlertTitle>Unable to run initial image analysis</AlertTitle>
        {selectionError || (!studyId ? 'Select a study first.' : 'Select at least one slide.')}
      </Alert>
    );
  }

  return (
    <ActionStepper
      activeStateHandlers={activeStateHandlers}
      handleSubmit={handleSubmit(onSubmit)}
      steps={steps}
      setActiveStepForValidation={setActiveStep}
      isStepFailed={{ ...isStepFailed, 0: Boolean(isStepFailed[0]) || isRerunReasonMissing }}
      validateStep={() => checkValidationAndSetIsStepFailed(activeStep)}
      finishDisabled={Boolean(size(invalidPipelineReviews)) || Boolean(mifSelection && !effectiveDnaChannel)}
      finishDisabledMessage={finishDisabledMessage}
    />
  );
};
